/**
 * QuotePixChargeCard — emite e acompanha a cobrança Pix de um orçamento.
 *
 * Aparece quando a detecção automática está valendo (`resolution.dynamic`) OU
 * quando já existe cobrança — o segundo caso importa porque desligar o
 * interruptor não desfaz uma cobrança já enviada ao cliente, e sumir com o card
 * deixaria o prestador sem ver o que está em aberto.
 *
 * O que a tela NÃO faz: prometer. Enquanto não há cobrança, ela diz que o
 * documento sai sem QR; quando há, diz o que está acontecendo. A tentação era
 * escrever "o pagamento será detectado automaticamente" já no estado vazio — e
 * aí o prestador manda o orçamento sem gerar a cobrança, o cliente recebe um
 * papel sem forma de pagamento, e ninguém vê erro nenhum (#9).
 */
import { useState } from 'react';
import { Copy, Loader2, QrCode, ExternalLink, CheckCircle2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { usePaymentDetection } from '@/hooks/usePaymentDetection';
import { useQuoteCharge } from '@/hooks/useQuoteCharge';
import { AsaasSeal } from '@/components/payments/AsaasSeal';
import { chargeIsUsable, chargeStatusLabel, isChargePaid, isChargePayable } from '@/lib/asaasCharge';
import { PAYOUT_STATE_LABELS, payoutNeedsAttention } from '@/lib/asaasPayout';

interface Props {
  quoteId: string;
  total: number;
  title?: string | null;
  sequenceId?: number | null;
  clientId?: string | null;
  client?: { name?: string | null; document?: string | null; email?: string | null; phone?: string | null };
}

export function QuotePixChargeCard({ quoteId, total, title, sequenceId, clientId, client }: Props) {
  const { resolution, visible } = usePaymentDetection();
  const { current, loading, creating, createCharge, payout, retryingPayout, retryPayout } =
    useQuoteCharge(quoteId);
  const [copied, setCopied] = useState(false);

  if (!visible) return null;
  if (!resolution.dynamic && !current) return null;

  const paid = isChargePaid(current?.status);
  const needsAttention = payoutNeedsAttention(payout);
  const payable = isChargePayable(current?.status);
  // Em aberto NÃO basta: o Asaas pode criar a cobrança e falhar ao gerar o QR.
  // Sem o código não há o que copiar nem o que imprimir, e prometer o contrário
  // faz a tela discordar do documento.
  const temCodigo = chargeIsUsable(current);

  const handleCreate = async () => {
    try {
      await createCharge({
        quoteId,
        value: total,
        description: `Orçamento${sequenceId ? ` #${sequenceId}` : ''}${title ? ` — ${title}` : ''}`.slice(0, 200),
        clientId,
        client: {
          name: client?.name,
          cpfCnpj: client?.document,
          email: client?.email,
          phone: client?.phone,
        },
      });
    } catch {
      // O toast já saiu no `onError` da mutation — aqui só evitamos o unhandled.
    }
  };

  const handleRetryPayout = async () => {
    try {
      await retryPayout();
    } catch {
      // O toast já saiu no `onError` da mutation.
    }
  };

  const handleCopy = async () => {
    const payload = current?.pix_payload;
    if (!payload) return;
    try {
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Código Pix copiado.');
    } catch {
      toast.error('Não foi possível copiar o código.');
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-start gap-3">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
            paid ? 'bg-success/15' : 'bg-primary/15'
          }`}
        >
          {paid ? (
            <CheckCircle2 className="h-5 w-5 text-success" />
          ) : (
            <QrCode className="h-5 w-5 text-primary" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm font-medium leading-tight">Cobrança Pix</p>
            {current && (
              <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                {chargeStatusLabel(current.status)}
              </span>
            )}
          </div>

          {loading ? (
            <p className="mt-1 text-xs text-muted-foreground">Carregando…</p>
          ) : !current ? (
            <>
              <p className="mt-1 text-xs text-muted-foreground">
                Gere o código para o cliente pagar. Enquanto não houver cobrança, o orçamento sai
                sem QR.
              </p>
              <Button3D size="sm" className="mt-3" disabled={creating} onClick={handleCreate}>
                {creating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Gerando…
                  </>
                ) : (
                  'Gerar cobrança Pix'
                )}
              </Button3D>
            </>
          ) : paid ? (
            <>
              <p className="mt-1 text-xs text-muted-foreground">
                O cliente pagou. A baixa no financeiro é automática.
              </p>

              {/*
                A SEGUNDA perna do dinheiro. "Pagou" e "chegou na sua conta" são
                eventos diferentes, e entre um e outro dá para falhar — foi o que
                aconteceu no primeiro repasse real, e o app ficou mudo com o
                valor retido. Nada aqui aparece enquanto a consulta não responde
                (estado `none`), para não acusar pendência durante o carregamento.
              */}
              {payout.state !== 'none' && (
                <div
                  className={`mt-2 rounded-lg px-2.5 py-2 text-xs ${
                    needsAttention
                      // Branco puro sobre a tinta âmbar dava 1,08 (#207).
                      ? 'bg-warning/10 font-medium text-amber-700 dark:text-amber-400'
                      : 'text-muted-foreground'
                  }`}
                >
                  <p>{PAYOUT_STATE_LABELS[payout.state]}</p>
                  {payout.error && (
                    <p className="mt-1 text-[11px] opacity-80">Motivo: {payout.error}</p>
                  )}
                  {payout.canRetry && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2"
                      disabled={retryingPayout}
                      onClick={handleRetryPayout}
                    >
                      {retryingPayout ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Pedindo…
                        </>
                      ) : (
                        <>
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Pedir repasse de novo
                        </>
                      )}
                    </Button>
                  )}
                </div>
              )}
            </>
          ) : payable && temCodigo ? (
            <>
              <p className="mt-1 text-xs text-muted-foreground">
                O orçamento já sai com o QR. Assim que o cliente pagar, o app dá baixa e avisa você.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={handleCopy}>
                  <Copy className="mr-2 h-4 w-4" />
                  {copied ? 'Copiado' : 'Copiar código'}
                </Button>
                {current.invoice_url && (
                  <Button size="sm" variant="outline" asChild>
                    <a href={current.invoice_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Abrir fatura
                    </a>
                  </Button>
                )}
              </div>
            </>
          ) : payable ? (
            <>
              {/* Cobrança criada, QR NÃO gerado. Aconteceu de verdade no primeiro
                  E2E de sandbox: o `POST /payments` deu certo e o
                  `GET /pixQrCode` falhou. A fatura do provedor continua servindo
                  para o cliente pagar, então ela é oferecida — mas o documento
                  sai sem QR, e dizer isso é o que impede a tela de prometer o
                  que o papel não entrega. */}
              <p className="mt-1 flex flex-col gap-1 rounded-lg bg-warning/10 px-2.5 py-2 text-xs font-medium text-amber-700 dark:text-amber-400">
                <span>
                  A cobrança foi criada, mas o código Pix não veio — o orçamento sai sem QR.
                </span>
                <span className="font-normal opacity-90">
                  Você ainda pode enviar o link da fatura, ou gerar outra cobrança.
                </span>
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {current.invoice_url && (
                  <Button size="sm" variant="outline" asChild>
                    <a href={current.invoice_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Abrir fatura
                    </a>
                  </Button>
                )}
                <Button size="sm" variant="outline" disabled={creating} onClick={handleCreate}>
                  {creating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Gerando…
                    </>
                  ) : (
                    'Gerar outra cobrança'
                  )}
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Cancelada, estornada ou contestada: a cobrança não vale mais e o
                  documento voltou a sair sem QR. Dizer isso é o que evita o
                  prestador mandar um orçamento que ninguém consegue pagar. */}
              <p className="mt-1 text-xs text-muted-foreground">
                Esta cobrança não está mais válida, e o orçamento saiu sem QR. Gere outra para o
                cliente conseguir pagar.
              </p>
              <Button3D size="sm" className="mt-3" disabled={creating} onClick={handleCreate}>
                {creating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Gerando…
                  </>
                ) : (
                  'Gerar outra cobrança'
                )}
              </Button3D>
            </>
          )}

          {/*
            Atribuição obrigatória (Resolução Conjunta nº 16/2025). Este card é o
            ponto de contato em que o dinheiro se move de verdade — a cobrança
            nasce, o QR sai e o repasse acontece —, então ele identifica quem
            presta o serviço financeiro. Fica FORA dos ramos de estado: a
            exigência não depende de a cobrança existir, estar paga ou ter sido
            cancelada.
          */}
          <div className="mt-3 border-t border-border pt-3">
            <AsaasSeal />
          </div>
        </div>
      </div>
    </div>
  );
}
